<form [formGroup]="formGroup()" class="user-form">
  <div class="form-row">
    <div class="form-field">
      <label [for]="fieldId('firstname')">
        {{ 'SETTINGS_ACCESS.USERS.FIRST_NAME' | translate }}
        @if (isCreate()) {
          <span class="required">*</span>
        }
      </label>
      <input
        pInputText
        [id]="fieldId('firstname')"
        type="text"
        formControlName="firstname"
        [placeholder]="'SETTINGS_ACCESS.USERS.FIRST_NAME_PLACEHOLDER' | translate"
        class="w-full"
        [class.ng-invalid]="!!errorMessage('firstname')"
        [attr.aria-invalid]="errorMessage('firstname') ? true : null"
        [attr.aria-describedby]="errorMessage('firstname') ? errorId('firstname') : null" />
      @if (
        isCreate() &&
        formGroup().get('firstname')?.invalid &&
        formGroup().get('firstname')?.touched
      ) {
        <small class="p-error">
          {{ 'SETTINGS_ACCESS.USERS.FIRST_NAME_REQUIRED' | translate }}
        </small>
      }
      <tf-form-field-error
        [message]="errorMessage('firstname')"
        [errorId]="errorId('firstname')" />
    </div>

    <div class="form-field">
      <label [for]="fieldId('lastname')">
        {{ 'SETTINGS_ACCESS.USERS.LAST_NAME' | translate }}
        @if (isCreate()) {
          <span class="required">*</span>
        }
      </label>
      <input
        pInputText
        [id]="fieldId('lastname')"
        type="text"
        formControlName="lastname"
        [placeholder]="'SETTINGS_ACCESS.USERS.LAST_NAME_PLACEHOLDER' | translate"
        class="w-full"
        [class.ng-invalid]="!!errorMessage('lastname')"
        [attr.aria-invalid]="errorMessage('lastname') ? true : null"
        [attr.aria-describedby]="errorMessage('lastname') ? errorId('lastname') : null" />
      @if (
        isCreate() &&
        formGroup().get('lastname')?.invalid &&
        formGroup().get('lastname')?.touched
      ) {
        <small class="p-error">
          {{ 'SETTINGS_ACCESS.USERS.LAST_NAME_REQUIRED' | translate }}
        </small>
      }
      <tf-form-field-error
        [message]="errorMessage('lastname')"
        [errorId]="errorId('lastname')" />
    </div>
  </div>

  <div class="form-field">
    <label [for]="fieldId('email')">
      {{ 'SETTINGS_ACCESS.USERS.EMAIL' | translate }}
      <span class="required">*</span>
    </label>
    <input
      pInputText
      [id]="fieldId('email')"
      type="email"
      formControlName="email"
      [placeholder]="'SETTINGS_ACCESS.USERS.EMAIL_PLACEHOLDER' | translate"
      class="w-full"
      [class.ng-invalid]="!!errorMessage('email')"
      [attr.aria-invalid]="errorMessage('email') ? true : null"
      [attr.aria-describedby]="errorMessage('email') ? errorId('email') : null" />
    @if (formGroup().get('email')?.invalid && formGroup().get('email')?.touched) {
      <small class="p-error">
        @if (isCreate() && formGroup().get('email')?.hasError('email')) {
          {{ 'SETTINGS_ACCESS.USERS.EMAIL_INVALID' | translate }}
        } @else {
          {{ 'SETTINGS_ACCESS.USERS.EMAIL_REQUIRED' | translate }}
        }
      </small>
    }
    <tf-form-field-error [message]="errorMessage('email')" [errorId]="errorId('email')" />
  </div>

  <div class="form-field">
    <label [for]="fieldId('roles')">
      {{ 'SETTINGS_ACCESS.USERS.ROLES' | translate }}
      @if (isCreate()) {
        <span class="required">*</span>
      }
    </label>
    <p-multiSelect
      [id]="fieldId('roles')"
      [options]="availableRoles()"
      formControlName="roles"
      [placeholder]="
        isEdit() && hasAnyRoles() ? '' : ('SETTINGS_ACCESS.USERS.ROLES_PLACEHOLDER' | translate)
      "
      [display]="'chip'"
      [showClear]="true"
      optionLabel="label"
      optionValue="value"
      appendTo="body"
      [virtualScroll]="isEdit()"
      [virtualScrollItemSize]="38"
      (onChange)="isEdit() ? onRolesChange($event.value) : null"
      (onClear)="isEdit() ? onRolesClear() : null">
      @if (isEdit()) {
        <ng-template pTemplate="selectedItems">
          @for (roleId of formGroup().get('roles')?.value ?? []; track roleId) {
            <p-chip
              [label]="getRoleLabel()(roleId)"
              [removable]="canAssignRole()(roleId)"
              (onRemove)="removeRole(roleId)"></p-chip>
          }
        </ng-template>
      }
      <ng-template #item let-option>
        <tf-option-with-info
          [label]="option.label"
          [tooltip]="
            ('SETTINGS_ACCESS.USERS.ROLE_NAME_TOOLTIP' | translate) + option.description
          " />
      </ng-template>
    </p-multiSelect>
    @if (
      isCreate() && formGroup().get('roles')?.invalid && formGroup().get('roles')?.touched
    ) {
      <small class="p-error">{{ 'SETTINGS_ACCESS.USERS.ROLES_REQUIRED' | translate }}</small>
    }
    <tf-form-field-error [message]="errorMessage('roles')" [errorId]="errorId('roles')" />
  </div>

  @if (canAssignSkills()) {
    <div class="form-field">
      <label [for]="fieldId('skills')">{{ 'SETTINGS_ACCESS.USERS.SKILLS' | translate }}</label>
      <p-multiSelect
        [id]="fieldId('skills')"
        [options]="availableSkills()"
        formControlName="skills"
        [placeholder]="'SETTINGS_ACCESS.USERS.SKILLS_PLACEHOLDER' | translate"
        [display]="'chip'"
        [showClear]="true"
        optionLabel="label"
        optionValue="value"
        appendTo="body"
        [filter]="true"
        [virtualScroll]="true"
        [virtualScrollItemSize]="38"
        [emptyFilterMessage]="'SETTINGS_ACCESS.USERS.NO_SKILLS_FOUND' | translate"
        [emptyMessage]="'SETTINGS_ACCESS.USERS.NO_SKILLS_FOUND' | translate">
      </p-multiSelect>
      <tf-form-field-error [message]="errorMessage('skills')" [errorId]="errorId('skills')" />
    </div>
  }

  <div class="form-field">
    <label [for]="fieldId('tags')">{{ 'SETTINGS_ACCESS.USERS.TAGS' | translate }}</label>
    <p-multiSelect
      [id]="fieldId('tags')"
      [options]="availableTagOptions()"
      formControlName="tags"
      [placeholder]="'SETTINGS_ACCESS.USERS.TAGS_PLACEHOLDER' | translate"
      [display]="'chip'"
      [showClear]="true"
      optionLabel="label"
      optionValue="value"
      appendTo="body"
      [filter]="true"
      [virtualScroll]="true"
      [virtualScrollItemSize]="38"
      [emptyFilterMessage]="'SETTINGS_ACCESS.USERS.NO_TAGS_FOUND' | translate"
      [emptyMessage]="'SETTINGS_ACCESS.USERS.NO_TAGS_FOUND' | translate">
      <ng-template let-option #item>
        <span class="tag-option">
          <span class="tag-option__dot" [style.background-color]="option.color"></span>
          <span>{{ option.label }}</span>
        </span>
      </ng-template>
    </p-multiSelect>
    <tf-form-field-error [message]="errorMessage('tags')" [errorId]="errorId('tags')" />
  </div>
</form>
